import { Icon, Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import { capitalCase } from "change-case";
import MDAlert from "components/MDAlert";
import MDTypography from "components/MDTypography";
import dayjs from "dayjs";
import useProposalPackageSummary from "hooks/proposals/useProposalPackageSummary";
import formatCentsToUSD from "utils/money/formatCentsToUSD";
import { Link } from "react-router-dom";
import { getRoute } from "utils/routing";

export const EXPANDED_CONTENT_COLSPAN = 11;

export default function ProposalExpandedRows({ proposal }: { proposal: any }) {
  const { data, loading, error } = useProposalPackageSummary(proposal.id);
  const packageChildren = data?.getProposal?.packageChildren ?? [];

  if (loading) {
    return (
      <TableRow>
        <TableCell></TableCell>
        <TableCell colSpan={EXPANDED_CONTENT_COLSPAN}>Loading proposal details...</TableCell>
      </TableRow>
    );
  }

  if (error) {
    return (
      <TableRow>
        <TableCell></TableCell>
        <TableCell colSpan={EXPANDED_CONTENT_COLSPAN}>
          <MDAlert color="error">
            Unable to load child proposals for this package. {error.message}
          </MDAlert>
        </TableCell>
      </TableRow>
    );
  }

  if (packageChildren.length > 0) {
    return (
      <TableRow>
        <TableCell></TableCell>
        <TableCell colSpan={EXPANDED_CONTENT_COLSPAN} sx={{ py: 2 }}>
          <MDTypography variant="button" fontWeight="bold" sx={{ display: "block", mb: 1.5 }}>
            Sent To
          </MDTypography>
          <Table
            size="small"
            sx={{
              "& .MuiTableHead-root": { display: "table-header-group" },
              "& .MuiTableRow-root": { display: "table-row" },
              "& .MuiTableCell-root": { display: "table-cell" },
            }}
          >
            <TableHead sx={{ display: "table-header-group" }}>
              <TableRow sx={{ display: "table-row" }}>
                <TableCell sx={{ display: "table-cell" }}>
                  <strong>Proposal</strong>
                </TableCell>
                <TableCell sx={{ display: "table-cell" }}>
                  <strong>Company</strong>
                </TableCell>
                <TableCell sx={{ display: "table-cell" }}>
                  <strong>Contact</strong>
                </TableCell>
                <TableCell sx={{ display: "table-cell" }}>
                  <strong>Status</strong>
                </TableCell>
                <TableCell sx={{ display: "table-cell" }}>
                  <strong>Sent</strong>
                </TableCell>
                <TableCell sx={{ display: "table-cell" }}>
                  <strong>Signed</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {packageChildren.map((child) => (
                <TableRow key={`package-child-${child.id}`} sx={{ display: "table-row" }}>
                  <TableCell sx={{ display: "table-cell" }}>
                    <Link
                      style={{ color: "inherit" }}
                      to={getRoute("proposals.info", [["proposalId", child.id]])}
                    >
                      {child.externalIdWithPrefix}
                    </Link>
                  </TableCell>
                  <TableCell sx={{ display: "table-cell" }}>{child.company?.name ?? "-"}</TableCell>
                  <TableCell sx={{ display: "table-cell" }}>
                    {`${child.contact?.firstName ?? ""} ${child.contact?.lastName ?? ""}`.trim() ||
                      "-"}
                  </TableCell>
                  <TableCell sx={{ display: "table-cell" }}>{capitalCase(child.status)}</TableCell>
                  <TableCell sx={{ display: "table-cell" }}>
                    {child.sentAt ? dayjs(child.sentAt).format("YYYY-MM-DD") : "-"}
                  </TableCell>
                  <TableCell sx={{ display: "table-cell" }}>
                    {child.signedAt ? dayjs(child.signedAt).format("YYYY-MM-DD") : "-"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableCell>
      </TableRow>
    );
  }

  return (
    <>
      <TableRow>
        <TableCell></TableCell>
        <TableCell>
          <strong>Job Name</strong>
        </TableCell>
        <TableCell>
          <strong>Address</strong>
        </TableCell>
        <TableCell>
          <strong>Products</strong>
        </TableCell>
        <TableCell>
          <strong>Job Total</strong>
        </TableCell>
      </TableRow>
      {proposal.stages.map((proposalStage) => (
        <TableRow key={proposalStage.id}>
          <TableCell></TableCell>
          <TableCell>{proposalStage.name ? proposalStage.name : "-"}</TableCell>
          <TableCell>{proposalStage?.address?.line1 ? proposalStage?.address?.line1 : "-"}</TableCell>
          <TableCell>
            {proposalStage.proposalProducts.length
              ? proposalStage.proposalProducts.map((product, index) => (
                  <span key={index}>
                    {product.productName}
                    {index + 1 === proposalStage.proposalProducts.length ? "" : ", "}
                  </span>
                ))
              : "-"}
          </TableCell>
          <TableCell>{formatCentsToUSD(proposalStage.overallTotal)}</TableCell>
        </TableRow>
      ))}
    </>
  );
}
